import MyContext from '@/shared/contexts/AppContext';
import { isNavItemActive } from '@/shared/navigation/navigation-active.utils';
import { getNavigationMenuForView, isNavMenuDivider } from '@/shared/navigation/navigation-menu.config';
import { getNavGroupIcon, getNavItemIcon } from '@/shared/navigation/navigation-menu-icons';
import { useNavigationView } from '@/shared/hooks/useNavigationView';
import { Link, useLocation } from 'react-router-dom';
import { APP_DISPLAY_VERSION } from '@/shared/constants/app-version';
import { Links } from '@/shared/constants/navegacao';
import {
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuCircleHelp,
} from 'react-icons/lu';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AppSidebarDoodles from '@/shared/components/layout/AppSidebarDoodles';
import FontSizeControl from '@/shared/components/layout/FontSizeControl';

const SIDEBAR_COLLAPSED_KEY = 'sgi-sidebar-collapsed';
const GROUP_EXPANDED_KEY = 'sgi-nav-group-expanded';
const GROUPS_COLLAPSED_KEY_LEGACY = 'sgi-nav-groups-collapsed';
const DPE_LOGO_SRC = '/img/LOGO_DPE_2024_ok.png';

function loadExpandedGroup(): string | null {
  try {
    const raw = localStorage.getItem(GROUP_EXPANDED_KEY);
    if (raw) return JSON.parse(raw) as string | null;

    const legacy = localStorage.getItem(GROUPS_COLLAPSED_KEY_LEGACY);
    if (legacy) {
      const collapsed = JSON.parse(legacy) as Record<string, boolean>;
      const expandedKeys = Object.keys(collapsed).filter((key) => collapsed[key] === false);
      return expandedKeys.at(-1) ?? null;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export default function AppSidebar() {
  const location = useLocation();
  const context = useContext(MyContext);
  const { effectiveView, permissions, viewReady } = useNavigationView();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(loadExpandedGroup);

  const sections = useMemo(
    () =>
      viewReady && effectiveView != null
        ? getNavigationMenuForView(effectiveView, permissions)
        : [],
    [viewReady, effectiveView, permissions],
  );

  useEffect(() => {
    for (const section of sections) {
      for (const group of section.groups) {
        const groupKey = `${section.title}::${group.label}`;
        const hasActiveItem = group.items.some(
          (entry) =>
            !isNavMenuDivider(entry) &&
            isNavItemActive(location.pathname, location.search, entry.to),
        );
        if (hasActiveItem) {
          setExpandedGroupKey(groupKey);
          try {
            localStorage.setItem(GROUP_EXPANDED_KEY, JSON.stringify(groupKey));
          } catch {
            /* ignore */
          }
          return;
        }
      }
    }
  }, [location.pathname, location.search, sections]);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroupKey((prev) => {
      const next = prev === key ? null : key;
      try {
        localStorage.setItem(GROUP_EXPANDED_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  if (!context) return null;

  return (
    <aside
      className={`app-sidebar d-print-none${sidebarCollapsed ? ' app-sidebar--collapsed' : ''}`}
      aria-label="Menu principal"
    >
      <AppSidebarDoodles />
      <div className="app-sidebar__brand">
        <Link
          to={Links.Index}
          className="app-sidebar__brand-link"
          title="Página inicial"
        >
          {!sidebarCollapsed && (
            <img
              src={DPE_LOGO_SRC}
              alt="Defensoria Pública do Estado de Sergipe"
              className="app-sidebar__brand-logo"
            />
          )}
          <span className="app-sidebar__brand-text">
            <span className="app-sidebar__logo">SGI</span>
            {!sidebarCollapsed && (
              <span className="app-sidebar__tagline">Sistema de Gestão Interna</span>
            )}
          </span>
        </Link>
        {!sidebarCollapsed && (
          <Link
            to={Links.ChangeLog}
            className="app-sidebar__version"
            title={APP_DISPLAY_VERSION}
          >
            {APP_DISPLAY_VERSION}
          </Link>
        )}
      </div>

      <nav className="app-sidebar__nav">
        {sections.map((section) => (
          <div key={section.title} className="app-sidebar__section">
            {!sidebarCollapsed && (
              <p className="app-sidebar__section-title">{section.title}</p>
            )}
            {(section.flatItems ?? []).map((navItem) => {
              const active = isNavItemActive(
                location.pathname,
                location.search,
                navItem.to,
              );
              const ItemIcon = getNavItemIcon(navItem.label);
              return (
                <Link
                  key={navItem.to + navItem.label}
                  to={navItem.to}
                  title={navItem.title ?? navItem.label}
                  className={`app-sidebar__link${active ? ' app-sidebar__link--active' : ''}`}
                >
                  <span className="app-sidebar__link-inner">
                    <ItemIcon className="app-sidebar__icon" size={18} aria-hidden />
                    {!sidebarCollapsed && (
                      <span className="app-sidebar__link-text">{navItem.label}</span>
                    )}
                  </span>
                  {!sidebarCollapsed && active && (
                    <span className="app-sidebar__active-dot" aria-hidden />
                  )}
                </Link>
              );
            })}
            {section.groups.map((group) => {
              const groupKey = `${section.title}::${group.label}`;
              const isGroupCollapsed = expandedGroupKey !== groupKey;
              const GroupIcon = getNavGroupIcon(group.label);

              return (
                <div key={groupKey} className="app-sidebar__group">
                  <button
                    type="button"
                    className="app-sidebar__group-toggle"
                    onClick={() => toggleGroup(groupKey)}
                    title={group.label}
                    aria-expanded={!isGroupCollapsed}
                  >
                    <span className="app-sidebar__group-toggle-left">
                      <GroupIcon className="app-sidebar__icon" size={20} aria-hidden />
                      {!sidebarCollapsed && (
                        <span className="app-sidebar__group-label">{group.label}</span>
                      )}
                    </span>
                    <LuChevronDown
                      className={`app-sidebar__chevron${isGroupCollapsed ? ' app-sidebar__chevron--collapsed' : ''}`}
                      size={16}
                      aria-hidden
                    />
                  </button>

                  <ul
                    className={`app-sidebar__list${isGroupCollapsed ? ' app-sidebar__list--hidden' : ''}`}
                  >
                    {group.items.map((entry) => {
                      if (isNavMenuDivider(entry)) {
                        return (
                          <li
                            key={`divider-${entry.label}`}
                            className="app-sidebar__divider"
                            aria-hidden={sidebarCollapsed}
                          >
                            {!sidebarCollapsed && (
                              <span className="app-sidebar__divider-label">
                                {entry.label}
                              </span>
                            )}
                          </li>
                        );
                      }

                      const navItem = entry;
                      const active = isNavItemActive(
                        location.pathname,
                        location.search,
                        navItem.to,
                      );
                      const ItemIcon = getNavItemIcon(navItem.label);
                      return (
                        <li key={navItem.to + navItem.label}>
                          <Link
                            to={navItem.to}
                            title={navItem.title ?? navItem.label}
                            className={`app-sidebar__link${active ? ' app-sidebar__link--active' : ''}`}
                          >
                            <span className="app-sidebar__link-inner">
                              <ItemIcon className="app-sidebar__icon" size={18} aria-hidden />
                              {!sidebarCollapsed && (
                                <span className="app-sidebar__link-text">{navItem.label}</span>
                              )}
                            </span>
                            {!sidebarCollapsed && active && (
                              <span className="app-sidebar__active-dot" aria-hidden />
                            )}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="app-sidebar__footer">
        <div className="app-sidebar__footer-tools">
          <div className="app-sidebar__font-size">
            <FontSizeControl />
          </div>
          <Link
            to={Links.Ajuda}
            className="app-sidebar__help-btn"
            title="Ajuda"
            aria-label="Ajuda"
          >
            <LuCircleHelp size={18} aria-hidden />
          </Link>
        </div>
        <button
          type="button"
          className="app-sidebar__collapse-btn"
          onClick={toggleSidebar}
          title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
          aria-label={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          {sidebarCollapsed ? (
            <LuChevronRight size={20} aria-hidden />
          ) : (
            <>
              <LuChevronLeft size={20} aria-hidden />
              <span>Recolher</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
